// @ts-expect-error - Ignore TS errors with import.meta for Vite environment variables
export const apiUrl = import.meta.env.VITE_DJANGO_URL;

export const authHeaders = (): HeadersInit => {
  const token = localStorage.getItem('token');
  if (!token) {
    return {
      'Content-Type': 'application/json',
    };
  }
  return {
    'Content-Type': 'application/json',
    Authorization: 'Token ' + token,
  };
};

/**
 * Authenticated fetch wrapper that dispatches session events.
 * - On 401: dispatches 'session-expired' event
 * - On success: dispatches 'session-activity' event to extend client-side session tracking
 *
 * Pass `background: true` for requests the user did not initiate (polling, prefetch).
 * Those still report a 401, but must not count as activity: SessionExpiryProvider
 * extends the expiry by a full TTL on every 'session-activity' event, so a request
 * on a timer would keep the session alive forever and defeat the idle timeout.
 */
export const authenticatedFetch = async (
  url: string,
  options: RequestInit = {},
  { background = false }: { background?: boolean } = {}
): Promise<Response> => {
  const response = await fetch(url, {
    ...options,
    headers: {
      ...authHeaders(),
      ...options.headers,
    },
  });

  if (response.status === 401) {
    window.dispatchEvent(new Event('session-expired'));
  } else if (response.ok && !background) {
    window.dispatchEvent(new Event('session-activity'));
  }

  return response;
};

export const userLogin = async (username: string, password: string) => {
  try {
    const response = await fetch(apiUrl + '/auth/login', {
      method: 'POST',
      body: JSON.stringify({ username, password }),
      headers: {
        'Content-Type': 'application/json',
      },
    });
    const json = await response.json();
    console.log('login response: ', json);
    return json;
  } catch (error) {
    return error;
  }
};

export const getUser = async () => {
  const response = await fetch(apiUrl + '/auth/user', {
    headers: authHeaders(),
  });

  // Check if response is unauthorized
  if (response.status === 401) {
    window.dispatchEvent(new Event('session-expired'));
    throw new Error('Unauthorized');
  }

  if (!response.ok) {
    throw new Error('Failed to fetch user');
  }

  const json = await response.json();
  // Signal session activity on successful authenticated response
  window.dispatchEvent(new Event('session-activity'));
  return json;
};

export const validatePasswordResetToken = async (token: string) => {
  const response = await fetch(`${apiUrl}/password_reset/validate_token/`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ token }),
  });

  const data = await response.json();
  return data;
};

export const confirmPasswordReset = async (token: string, password: string) => {
  const response = await fetch(`${apiUrl}/password_reset/confirm/`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      token,
      password,
    }),
  });

  const result = await response.json();

  if (!response.ok) {
    throw new Error(result.detail || 'Error resetting password');
  }

  return result;
};

export const confirmSetPassword = async (token: string, password: string) => {
  const response = await fetch(`${apiUrl}/medpro/set_password_confirm/`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ token, password }),
  });

  const result = await response.json();

  if (!response.ok) {
    throw new Error(result.detail || 'Error setting password');
  }

  return result;
};

export const requestPasswordReset = async (email: string) => {
  const response = await fetch(`${apiUrl}/v2/admin_forgot_password`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ email }),
  });

  if (!response.ok) {
    throw new Error('Network error');
  }

  return response.json();
};
